import type { NavItemDataProps } from "@/components/nav/types";
import { Icon } from "@/components/icon";
import Logo from "@/components/logo";
import { NavMini, NavVertical } from "@/components/nav";
import type { NavProps } from "@/components/nav/types";
import { GLOBAL_CONFIG } from "@/global-config";
import useLocale from "@/locales/use-locale";
import { useSettingActions, useSettings } from "@/store/settingStore";
import { ThemeLayout } from "@/types/enum";
import { useMemo, useState } from "react";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { ScrollArea } from "@/ui/scroll-area";
import { cn } from "@/utils";

type Props = {
	data: NavProps["data"];
	className?: string;
};

function filterNavItems(items: NavItemDataProps[], query: string, t: (key: string) => string): NavItemDataProps[] {
	const lower = query.trim().toLowerCase();
	if (!lower) return items;

	return items
		.filter((item) => {
			const titleMatch = t(item.title).toLowerCase().includes(lower);
			const pathMatch = item.path.toLowerCase().includes(lower);
			const childMatch = item.children?.length ? filterNavItems(item.children, query, t).length > 0 : false;
			return titleMatch || pathMatch || childMatch;
		})
		.map((item) => ({
			...item,
			children: item.children?.length ? filterNavItems(item.children, query, t) : undefined,
		}));
}

function filterNavDataBySearch(data: NavProps["data"], query: string, t: (key: string) => string): NavProps["data"] {
	const lower = query.trim().toLowerCase();
	if (!lower) return data;

	return data
		.map((group) => {
			const nameMatches = group.name ? t(group.name).toLowerCase().includes(lower) : false;
			const filteredItems = filterNavItems(group.items, query, t);
			if (nameMatches) return { ...group, items: group.items };
			if (filteredItems.length === 0) return null;
			return { ...group, items: filteredItems };
		})
		.filter((group): group is NonNullable<typeof group> => group !== null);
}

export function NavVerticalLayout({ data, className }: Props) {
	const settings = useSettings();
	const { themeLayout } = settings;
	const { setSettings } = useSettingActions();
	const { t } = useLocale();
	const [searchQuery, setSearchQuery] = useState("");

	const filteredData = useMemo(() => filterNavDataBySearch(data, searchQuery, t), [data, searchQuery, t]);

	const navWidth = themeLayout === ThemeLayout.Vertical ? "var(--layout-nav-width)" : "var(--layout-nav-width-mini)";
	const handleToggle = () => {
		setSettings({
			...settings,
			themeLayout: themeLayout === ThemeLayout.Mini ? ThemeLayout.Vertical : ThemeLayout.Mini,
		});
	};

	const showSearchBar = themeLayout === ThemeLayout.Vertical;

	return (
		<nav
			data-slot="slash-layout-nav"
			className={cn("fixed inset-y-0 left-0 flex flex-col h-full bg-background border-r border-dashed z-nav transition-[width] duration-300 ease-in-out", className)}
			style={{
				width: navWidth,
			}}
		>
			<div
				className={cn("relative flex items-center py-4 px-2 h-(--layout-header-height) ", {
					"justify-center": themeLayout === ThemeLayout.Mini,
				})}
			>
				<div className="flex items-center justify-center">
					<Logo size={100} />
					<span
						className="text-l font-bold transition-all duration-300 ease-in-out"
						style={{
							opacity: themeLayout === ThemeLayout.Mini ? 0 : 1,
							maxWidth: themeLayout === ThemeLayout.Mini ? 0 : "auto",
							whiteSpace: "nowrap",
							marginLeft: themeLayout === ThemeLayout.Mini ? 0 : "8px",
						}}
					>
						{GLOBAL_CONFIG.appName}
					</span>
				</div>

				<Button variant="outline" size="icon" onClick={handleToggle} className="h-7 w-7 absolute right-0 translate-x-1/2">
					{themeLayout === ThemeLayout.Mini ? <Icon icon="lucide:arrow-right-to-line" size={12} /> : <Icon icon="lucide:arrow-left-to-line" size={12} />}
				</Button>
			</div>

			{showSearchBar && (
				<div className="shrink-0 px-2 pb-2">
					<div className="relative">
						<Icon
							icon="solar:magnifer-outline"
							size={18}
							className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
						/>
						<Input
							type="search"
							placeholder="Menü keresése..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="pl-8 h-8 text-sm"
							aria-label="Menü keresése"
						/>
					</div>
				</div>
			)}

			<ScrollArea className={cn("flex-1 min-h-0 px-2 bg-background")}>
				{themeLayout === ThemeLayout.Mini ? <NavMini data={data} /> : <NavVertical data={filteredData} />}
			</ScrollArea>
		</nav>
	);
}
